import { useState, useEffect } from 'react';
import { RefreshCw, X } from 'lucide-react';

export default function UpdateNotification() {
  const [showUpdatePrompt, setShowUpdatePrompt] = useState(false);
  const [newWorker, setNewWorker] = useState<ServiceWorker | null>(null);

  useEffect(() => {
    if ('serviceWorker' in navigator) {
      let registration: ServiceWorkerRegistration;

      const handleUpdateFound = () => {
        const newSW = registration.installing;
        if (newSW) {
          const handleStateChange = () => {
            if (newSW.state === 'installed' && navigator.serviceWorker.controller) {
              console.log('🔄 [PWA] New content available');
              setNewWorker(newSW);
              setShowUpdatePrompt(true);
            }
          };
          newSW.addEventListener('statechange', handleStateChange);
        }
      };

      const handleControllerChange = () => {
        console.log('🔄 [PWA] New service worker is now controlling the app');
        window.location.reload();
      };

      navigator.serviceWorker.ready.then((reg) => {
        registration = reg;
        registration.addEventListener('updatefound', handleUpdateFound);
      });

      navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange);

      // Cleanup function
      return () => {
        if (registration) {
          registration.removeEventListener('updatefound', handleUpdateFound);
        }
        navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange);
      };
    }
  }, []);

  const handleUpdate = () => {
    if (newWorker) {
      // Tell the new service worker to skip waiting and become active
      newWorker.postMessage({ type: 'SKIP_WAITING' });
      setShowUpdatePrompt(false);
    }
  };

  const dismissUpdate = () => {
    setShowUpdatePrompt(false);
    // Don't show again until next update
    sessionStorage.setItem('update-dismissed', 'true');
  };

  if (!showUpdatePrompt) {
    return null;
  }

  return (
    <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:max-w-sm z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-green-600 rounded-lg flex items-center justify-center">
              <RefreshCw className="w-4 h-4 text-white" />
            </div>
            <h3 className="font-semibold text-gray-900 dark:text-white">
              Update Available
            </h3>
          </div>
          <button
            onClick={dismissUpdate}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
          A new version of LoppeHub is available with improvements and new features.
        </p>

        <div className="flex space-x-2">
          <button
            onClick={handleUpdate}
            className="flex-1 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center space-x-1"
          >
            <RefreshCw className="w-3 h-3" />
            <span>Update Now</span>
          </button>
          <button
            onClick={dismissUpdate}
            className="px-4 py-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 text-sm font-medium transition-colors"
          >
            Later
          </button>
        </div>
      </div>
    </div>
  );
}
